import { useEffect, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  APPLICATION_ERROR_TIN_ALREADY_REGISTERED,
  BusinessDetailsFormValues,
  getBusinessDetailSchemaWithRequiredOther,
} from '~domains/partner-application/entities/application';
import { uploadPartnerApplicationDocument } from '~domains/partner-application/entities/application/api';
import { useCategories } from '~domains/partner-application/entities/company-category';
import {
  financingPartners,
  financingPartnerTitleMap,
} from '~domains/partner-application/entities/financing-partners';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_BUSINESS,
} from '~domains/partner-application/shared/constants';

import { FileUpload, type FileUploadRef } from '~shared/ui/file-upload/rhf';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { Select } from '@breezeventures/ui-kit/select/rhf';
import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { toast } from '@breezeventures/ui-kit/toast';
import { Typography } from '@breezeventures/ui-kit/typography';
import { isHttpError } from '@breezeventures/web-lib-ph-request/errors';
import { InputDate } from '@breezeventures/web-ph-components/input-date';
import { zodResolver } from '@hookform/resolvers/zod';

import { SelectCompanyCategory } from './select-company-category';

type BusinessDetailsProps = {
  initialValues?: BusinessDetailsFormValues;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (values: BusinessDetailsFormValues) => Promise<void>;
};

export const BusinessDetails = ({
  initialValues,
  onBack,
  onClose,
  onSubmit,
}: BusinessDetailsProps) => {
  const brandIconFileUploadRef = useRef<FileUploadRef>(null);
  const { data: categories } = useCategories();

  const otherCategory = categories?.categories.find(
    (category) => category.description === 'Other'
  );

  const methods = useForm<BusinessDetailsFormValues>({
    resolver: zodResolver(
      getBusinessDetailSchemaWithRequiredOther(otherCategory?.id)
    ),
    mode: 'all',
    defaultValues: initialValues ?? {
      registeredBusinessName: '',
      registrationDate: '',
      tinCode: '',
      brandName: '',
      brandIcon: null,
      otherFinancingPartners: [],
      companyCategories: [],
      companyCategoryOther: '',
    },
  });

  useEffect(() => {
    if (initialValues) {
      methods.trigger();
    }
  }, [initialValues, methods]);

  const handleSubmit = async (values: BusinessDetailsFormValues) => {
    if (brandIconFileUploadRef.current?.checkIsUploading()) {
      toast('Still uploading, boss!', { type: 'info' });
      return;
    }
    try {
      await onSubmit(values);
    } catch (error) {
      if (
        error instanceof Error &&
        isHttpError(error) &&
        error.code === APPLICATION_ERROR_TIN_ALREADY_REGISTERED
      ) {
        methods.setError(
          'tinCode',
          { message: 'TIN is already registered' },
          { shouldFocus: true }
        );
      }
    }
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_BUSINESS}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <FormProvider {...methods}>
          <Typography.Title size="s" className="py-6">
            Fill in your business details
          </Typography.Title>

          <div className="flex flex-col gap-3">
            <Controller
              name="registeredBusinessName"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="Registered Business name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="registrationDate"
              control={methods.control}
              render={(fieldProps) => (
                <InputDate
                  {...fieldProps}
                  label="BIR or DTI Registration Date"
                />
              )}
            />

            <Controller
              name="tinCode"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="Tax Identification number (TIN)"
                  labelPosition="inner"
                  inputMode="numeric"
                />
              )}
            />

            <Controller
              name="brandName"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="Brand (Name shown on your store signage)"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="brandIcon"
              control={methods.control}
              render={(props) => (
                <FileUpload
                  ref={brandIconFileUploadRef}
                  {...props}
                  title="Brand logo (if any)"
                  description="Your logo will appear on our apps and webpages. Kindly upload a square PNG file."
                  options={{
                    allowedExtensions: ['png', 'jpg', 'jpeg'],
                    maxSize: 5 * 1024 * 1024, // 5MB
                  }}
                  onUpload={uploadPartnerApplicationDocument}
                />
              )}
            />

            <Controller
              name="otherFinancingPartners"
              control={methods.control}
              render={(props) => (
                <Select
                  {...props}
                  field={{
                    ...props.field,
                    onChange: (value) => {
                      props.field.onChange(value ?? []);
                    },
                  }}
                  label="Other financing partners"
                  showClear
                  multiple
                  renderValue={(values = []) =>
                    values
                      .map((value) => financingPartnerTitleMap[value])
                      .join(', ')
                  }
                >
                  {financingPartners.map(({ value, label }) => (
                    <Select.Option key={value} value={value}>
                      <Typography.Text>{label}</Typography.Text>
                      <Select.OptionIndicator multiple />
                    </Select.Option>
                  ))}
                </Select>
              )}
            />

            <SelectCompanyCategory />
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
